import { useState } from "react";

import {
  DateInput,
  GoogleMapAutoComplete,
  Input,
  Label,
  PhoneInput,
  TextArea,
} from "@web-app/components";
import { FieldValue, Order, OrderDeliveryType } from "@web-app/types";
import { isSameDate } from "@web-app/utils/time";

import AddressDetailsAccordion from "./AddressDetailsAccordion";
import DateChangeModal from "./DateChangeModal";

interface PickupDetailsTabProps {
  debouncedHandleFieldChange: (property: string, value: FieldValue) => void;
  onFieldChange: (
    property: string,
    value: FieldValue,
    fromTab?: "dropoff" | "pickup",
  ) => void;
  order: Order;
}

export default function PickupDetailsTab({
  order,
  onFieldChange,
  debouncedHandleFieldChange,
}: PickupDetailsTabProps) {
  const isP2PDelivery =
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;

  const [newStartDate, setNewStartDate] = useState<Date | null>(null);

  // DateInput already converts the selected time from order's timezone to UTC,
  // so we just need to call toISOString() directly
  const onStartDateChange = (date: Date) => {
    onFieldChange("scheduledStartDateTime", date.toISOString(), "pickup");
  };

  return (
    <div className="space-y-3 lg:space-y-4 px-4 pb-10 lg:pb-20">
      <div className="flex flex-col gap-1">
        <Label required>Full Name</Label>
        <Input
          defaultValue={order.pickup?.name || ""}
          onChange={(value: string) =>
            debouncedHandleFieldChange("pickup.name", value)
          }
          placeholder="Enter pickup name"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label required>Phone Number</Label>
        <PhoneInput
          defaultValue={order.pickup?.phone || ""}
          onChange={(value: string) =>
            debouncedHandleFieldChange("pickup.phone", value)
          }
        />
      </div>

      <div className="flex flex-col gap-0">
        <div className="flex flex-col gap-1">
          <Label required>Address</Label>
          <GoogleMapAutoComplete
            // @ts-ignore - address is not a required field
            address={{ description: order.pickup?.address || "" }}
            setAddress={async (address) => {
              await onFieldChange("pickup.address", address);
            }}
            placeholder="Enter pickup location"
          />
        </div>

        <AddressDetailsAccordion
          addressType="pickup"
          debouncedHandleFieldChange={debouncedHandleFieldChange}
          location={order.pickup}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <Label required>
            {isP2PDelivery
              ? "Pickup At (Start Time)"
              : "Pickup After (Date/Time)"}
          </Label>
          <DateInput
            value={new Date(order.scheduledStartDateTime)}
            onChange={(date) => {
              if (isSameDate(date, order.scheduledStartDateTime)) {
                onStartDateChange(date);
              } else {
                setNewStartDate(date);
              }
            }}
            timezone={order.timezone}
            position="under"
          />
        </div>

        {newStartDate && (
          <DateChangeModal
            startDate={newStartDate}
            orderTimezone={order.timezone}
            onDateChangeCancel={() => setNewStartDate(null)}
            onDateChangeConfirm={() => {
              onStartDateChange(newStartDate);
              setNewStartDate(null);
            }}
          />
        )}

        {!isP2PDelivery && (
          <div className="flex flex-col gap-1">
            <Label required>Pickup Before (Date/Time)</Label>
            <DateInput
              value={new Date(order.scheduledEndDateTime)}
              onChange={(date) =>
                onFieldChange(
                  "scheduledEndDateTime",
                  date.toISOString(),
                  "pickup",
                )
              }
              timezone={order.timezone}
              position="under"
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Label>Business Name</Label>
        <Input
          defaultValue={order.pickup?.businessName || ""}
          onChange={(value: string) =>
            debouncedHandleFieldChange("pickup.businessName", value)
          }
          placeholder="Enter business name"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Note</Label>
        <TextArea
          placeholder="Enter pickup note"
          rows={3}
          value={order.pickup.note}
          onConfirm={(value) => {
            onFieldChange("pickup.note", value);
          }}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Order Requirements</Label>
        <Input
          type="checkbox"
          rightLabel="Signature Required"
          value={order.pickupRequirements.signature ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange("pickupRequirements.signature", value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Photo Required"
          value={order.pickupRequirements.photo ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange("pickupRequirements.photo", value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Notes Required"
          value={order.pickupRequirements.note ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange("pickupRequirements.note", value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Item Checklist Required"
          value={order.pickupRequirements.itemChecklist ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange("pickupRequirements.itemChecklist", value === "true")
          }
        />
      </div>
    </div>
  );
}
